<template>
    <div class="progress-circle">
        <svg 
            :width="computedSize" 
            :height="computedSize" 
            viewBox="0 0 126 127" 
            fill="none"
        >
            <!-- Фоновый круг -->
            <circle 
                cx="63" 
                cy="62" 
                :r="backgroundRadius" 
                :stroke="bgColor" 
                :stroke-width="bgStrokeWidth" 
            />
        
            <!-- Прогресс-круг с тенью -->
            <g :filter="`url(#${filterId})`">
                <circle 
                    cx="63" 
                    cy="62" 
                    :r="progressRadius"
                    :stroke="progressColor"
                    :stroke-width="progressStrokeWidth"
                    :stroke-dasharray="circumference"
                    :stroke-dashoffset="strokeDashOffset"
                    :transform="`rotate(-90 63 62)`"
                    class="progress-ring"
                />
            </g>
        
            <!-- Фильтр для тени -->
            <defs>
                <filter 
                    :id="filterId" 
                    x="0.0500002" 
                    y="0.37" 
                    width="125.9" 
                    height="125.9" 
                    filterUnits="userSpaceOnUse" 
                    color-interpolation-filters="sRGB"
                >
                    <feFlood flood-opacity="0" result="BackgroundImageFix"/>
                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                        <feOffset dy="1.32"/>
                        <feGaussianBlur :stdDeviation="blurIntensity"/>
                        <feComposite in2="hardAlpha" operator="out"/>
                    <feColorMatrix type="matrix" :values="shadowColorMatrixValues"/>
                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow"/>
                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape"/>
                </filter>
            </defs>
        </svg>

        <div class="progress-circle__wrap" :style="wrapStyles">
            <div v-if="!transparent" class="progress-circle-shadow" :style="shadowStyles" />
            <div class="progress-circle__content" :class="{ 'progress-circle__content_transparent': transparent }" >
                <slot />
            </div>
        </div>
    </div>
</template>
  
<script>
export default {
    name: 'ProgressCircle',
    props: {
        // Текущее значение прогресса
        value: {
            type: Number,
            default: 0
        },
        // Минимальное значение (0%)
        min: {
            type: Number,
            default: 0
        },
        // Максимальное значение (100%)
        max: {
            type: Number,
            default: 100,
            validator: v => typeof v === 'number' && !isNaN(v)
        },
        // Размер компонента
        size: {
            type: [Number, String],
            default: 126
        },
        // Цвет прогресса
        progressColor: {
            type: String,
            default: '#54B600'
        },
        // Цвет фона
        bgColor: {
            type: String,
            default: '#F4F5F8'
        },
        // Интенсивность размытия тени
        blurIntensity: {
            type: Number,
            default: 2.475
        },
        // Цвет тени
        shadowColor: {
            type: String,
            default: '#54B600',
            validator: value => /^#([0-9A-F]{3}){1,2}$/i.test(value)
        },
        // Прозрачность тени (0-1)
        shadowOpacity: {
            type: Number,
            default: 0.8,
            validator: v => v >= 0 && v <= 1
        },
        boxShadow: {
            type: String,
            default: '0 19.81px 70px rgba(22, 199, 45, 0.25)'
        },
        sizeWidget: {
            type: String,
            default: 'large'
        },
        transparent: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            filterId: `progress_filter_${Math.random().toString(36).substr(2, 9)}`,
            backgroundRadius: 57.67,
            progressRadius: 57.175,
            bgStrokeWidth: 2,
            progressStrokeWidth: 2
        }
    },
    computed: {
        // Преобразуем размер в пиксели
        computedSize() {
            return typeof this.size === 'number' 
            ? `${this.size}px` 
            : this.size
        },
        // Рассчитываем длину окружности
        circumference() {
            return 2 * Math.PI * this.progressRadius;
        },
        // Рассчитываем процент заполнения (0-1)
        progressFraction() {
            // Проверяем валидность диапазона
            if ( typeof this.min !== 'number' || typeof this.max !== 'number' || this.max <= this.min ) 
            {
                console.error('ProgressCircle: Invalid range [min, max]', this.min, this.max)
                return 0
            }
            
            // Ограничиваем значение в диапазоне [min, max]
            const clampedValue = Math.min( Math.max( this.value, this.min ), this.max )
            return ( clampedValue - this.min ) / ( this.max - this.min )
        },
        // Рассчитываем смещение штрихов
        strokeDashOffset() {
            return this.circumference * (1 - this.progressFraction);
        },
        // Преобразуем цвет тени в матрицу значений
        shadowColorMatrixValues() {
            const rgb = this.hexToRgb(this.shadowColor);
            if (!rgb) return "0 0 0 0 0.647059 0 0 0 0 0.917647 0 0 0 0 0.419608 0 0 0 0.8 0";
        
            return `0 0 0 0 ${rgb.r/255} 0 0 0 0 ${rgb.g/255} 0 0 0 0 ${rgb.b/255} 0 0 0 ${this.shadowOpacity} 0`;
        },
        wrapStyles() {
            const paddingValue = parseInt(this.bgStrokeWidth) + ( this.sizeWidget === 'large' ? 8 : 4 )
            const currentSize = typeof this.size === 'string' && this.size.includes( 'px' )
                ? this.size
                : `${this.size}px` 

            return { padding: `${paddingValue}px`, width: currentSize, height: currentSize }
        },
        shadowStyles() {
            return { boxShadow: this.boxShadow }
        }
    },
    watch: {
        min(newVal) {
            this.validateRange( newVal, this.max )
        },
        max(newVal) {
            this.validateRange( this.min, newVal )
        }
    },
    methods: {
        validateRange(min, max) {
            if ( typeof min !== 'number' || typeof max !== 'number' || max <= min )
            {
                console.error('ProgressCircle: max must be greater than min', min, max)
            }
        },
        hexToRgb(hex) {
            const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
            return result ? {
                r: parseInt(result[1], 16),
                g: parseInt(result[2], 16),
                b: parseInt(result[3], 16)
            } : null
        }
    }
}
</script>
  
<style scoped>
.progress-circle {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    max-width: 100%;
    border-radius: 50%;
}

.progress-ring {
    transition: stroke-dashoffset 0.5s ease;
}

.progress-circle__wrap {
    position: absolute;
    left: 50%;
    right: 0;
    top: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 50%;
    transform: translateX(-50%);
}

.progress-circle-shadow {
    position: absolute;
    left: 5px;
    right: 5px;
    top: 5px;
    bottom: 5px;
    border-radius: 50%;
    background: transparent;
}

.progress-circle__content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background: #FFFFFF;
    
    &.progress-circle__content_transparent {
        background: transparent;
    }
}
</style>